/**
 * A child's mistakes over time — م-3-02's "منحنى أخطائه".
 *
 * The turns arrive newest-first, because that is the order a parent reads
 * their history in; a curve drawn in that order runs backwards through time,
 * so this sorts before it does anything else.
 */

/** What the curve needs from a turn; a ChildRecitation satisfies it. */
export type TurnLike = { id: string; errors: number; endedAt: number };

export type TrendPoint = { id: string; errors: number; endedAt: number };

/** Which way the mistakes are going, or that nobody can yet say. */
export type TrendDirection = 'better' | 'steady' | 'worse' | 'unknown';

export type ErrorTrend = {
  points: TrendPoint[];
  direction: TrendDirection;
  /** The tallest bar the drawing has to fit; never zero, so it stays drawable. */
  worst: number;
  /** The two halves being compared, for a caller that wants to say them. */
  earlier: number;
  later: number;
};

/**
 * A quarter of a mistake per turn: below this the two halves are the same
 * level and the honest word is "steady". Without a band, four turns at 3, 4,
 * 3, 4 would be reported as an improvement.
 */
const SAME = 0.25;

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

export function errorTrend(turns: TurnLike[]): ErrorTrend {
  const points = [...turns]
    .filter((t) => t && Number.isFinite(t.errors) && Number.isFinite(t.endedAt))
    .sort((a, b) => a.endedAt - b.endedAt)
    .map((t) => ({ id: t.id, errors: Math.max(0, Math.round(t.errors)), endedAt: t.endedAt }));

  const errors = points.map((p) => p.errors);
  // A flat run of clean turns is the BEST case, not a missing one: the scale
  // still has to be a positive number for anything to be drawn against it.
  const worst = Math.max(1, ...errors);

  if (points.length < 2) {
    return { points, direction: 'unknown', worst, earlier: 0, later: 0 };
  }

  /*
   * Halves, not last-versus-first: a single bad day at the end of a good
   * month would otherwise read as "getting worse", which is both wrong and
   * discouraging to the child it is said about. An odd count gives the extra
   * turn to the later half, where it belongs.
   */
  const cut = Math.floor(points.length / 2);
  const earlier = mean(errors.slice(0, cut));
  const later = mean(errors.slice(cut));
  const change = later - earlier;

  const direction: TrendDirection = Math.abs(change) < SAME
    ? 'steady'
    : change < 0 ? 'better' : 'worse';

  return { points, direction, worst, earlier, later };
}
